import { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import Btn from '../../../../UI/Btn'
import Confetti from '../../../../UI/Confetti'

import { QUIZZES } from './quizzes'

import styles from './quiz.module.scss'

/**
 * Das Quiz eines Kapitels.
 *
 * Eine Frage nach der anderen mit Fortschrittsbalken — nicht alle fünf
 * untereinander: Wer die Liste sieht, überfliegt sie und beantwortet die
 * leichten zuerst.
 *
 * Weiter geht es erst nach einer Antwort. Nach dem Prüfen bleibt jede Frage
 * lesbar, mit der richtigen Antwort und der Erklärung dazu; wiederholt werden
 * darf beliebig oft.
 *
 * Ob das Kapitel damit bestanden ist, entscheidet nicht diese Komponente,
 * sondern der Server: Die Hürde steht in den Einstellungen und kommt als
 * `quizPass` mit dem Stand herein.
 *
 * **Wer besteht, kommt weiter.** Bis 23.08.2026 stand nach dem Bestehen nur
 * „Nochmal versuchen" da — der einzige Knopf führte also zurück in dieselbe
 * Runde, obwohl es nichts mehr zu holen gab. Jetzt gibt es Konfetti und den
 * Weg ins nächste Kapitel; wiederholen darf man weiter, nur eben nicht mehr
 * als einzige Möglichkeit.
 */
export default function Quiz({ chapter, quizPass, stand, onSubmit, onWeiter, weiterLabel }) {
	const fragen = QUIZZES[chapter] || []

	const [at, setAt] = useState(0)
	const [antworten, setAntworten] = useState({})
	const [geprueft, setGeprueft] = useState(false)
	const [sende, setSende] = useState(false)
	// Zählt die bestandenen Durchgänge — jeder neue Wert lässt Konfetti fallen.
	const [jubel, setJubel] = useState(0)

	/*
	 * Beim Kapitelwechsel muss das Quiz leer beginnen. Die Eltern rendern
	 * dieselbe Instanz für jedes Kapitel — ohne diesen Rücksetzer standen die
	 * Antworten und das Ergebnis des vorigen Kapitels im nächsten schon drin
	 * (#165). Hier statt per key beim Aufrufer, damit kein Aufrufer daran
	 * denken muss.
	 */
	const [gezeigtesKapitel, setGezeigtesKapitel] = useState(chapter)
	if (chapter !== gezeigtesKapitel) {
		setGezeigtesKapitel(chapter)
		setAt(0)
		setAntworten({})
		setGeprueft(false)
	}

	if (!fragen.length) return null

	const richtige = fragen.filter((frage, i) => antworten[i] === frage.c).length
	const bestanden = richtige >= quizPass
	const beantwortet = antworten[at] !== undefined

	const pruefen = () => {
		setGeprueft(true)
		setSende(true)
		setAt(0)
		if (bestanden) setJubel(wert => wert + 1)
		Promise.resolve(onSubmit({ chapter, correct: richtige, total: fragen.length })).finally(() => setSende(false))
	}

	const nochmal = () => {
		setAntworten({})
		setGeprueft(false)
		setAt(0)
	}

	const frage = fragen[at]

	return (
		<section className={styles.root}>
			<Confetti ausloeser={jubel} />

			<header className={styles.head}>
				<h3>Quiz zu Kapitel {chapter}</h3>
				<span
					className={clsx(
						styles.pill,
						geprueft && (bestanden ? styles.pillPass : styles.pillFail),
						!geprueft && stand?.passed && styles.pillPass
					)}
				>
					{geprueft
						? `${richtige} von ${fragen.length} richtig`
						: stand
						? `Bisher bestes Ergebnis: ${stand.correct} von ${stand.total || fragen.length}`
						: `${fragen.length} Fragen · ${quizPass} richtige zum Bestehen`}
				</span>
			</header>

			<div className={styles.track}>
				<i style={{ width: `${((geprueft ? fragen.length : at + 1) / fragen.length) * 100}%` }} />
			</div>

			<div className={styles.body}>
				<p className={styles.step}>
					Frage {at + 1} von {fragen.length}
				</p>
				<p className={styles.question}>{frage.q}</p>

				<div className={styles.options}>
					{frage.o.map((option, oi) => {
						const gewaehlt = antworten[at] === oi
						const richtig = geprueft && oi === frage.c
						const falsch = geprueft && gewaehlt && oi !== frage.c

						return (
							<label
								key={oi}
								className={clsx(
									styles.option,
									gewaehlt && !geprueft && styles.picked,
									richtig && styles.right,
									falsch && styles.wrong
								)}
							>
								<input
									type="radio"
									name={`quiz-${chapter}-${at}`}
									checked={gewaehlt}
									disabled={geprueft}
									onChange={() => setAntworten(prev => ({ ...prev, [at]: oi }))}
								/>
								<span>
									{richtig && <b>✓ </b>}
									{falsch && <b>✕ </b>}
									{option}
								</span>
							</label>
						)
					})}
				</div>

				{geprueft && <p className={styles.explain}>{frage.e}</p>}
			</div>

			<footer className={styles.foot}>
				{at > 0 && (
					<Btn theme="secondary" onClick={() => setAt(at - 1)}>
						Zurück
					</Btn>
				)}
				{at < fragen.length - 1 && (
					<Btn disabled={!geprueft && !beantwortet} onClick={() => setAt(at + 1)}>
						{geprueft ? 'Nächste Erklärung' : 'Weiter'}
					</Btn>
				)}
				{!geprueft && at === fragen.length - 1 && (
					<Btn disabled={!beantwortet} onClick={pruefen}>
						Antworten prüfen
					</Btn>
				)}
				{/*
				 * Bestanden: Der Weg nach vorn ist der Hauptknopf, das Wiederholen
				 * daneben. Nicht bestanden: umgekehrt — dann gibt es nach vorn
				 * nichts zu holen.
				 */}
				{geprueft && bestanden && onWeiter && <Btn onClick={onWeiter}>{weiterLabel}</Btn>}
				{geprueft && (
					<Btn theme="secondary" onClick={nochmal}>
						Nochmal versuchen
					</Btn>
				)}

				{geprueft && (
					<span className={clsx(styles.result, bestanden ? styles.good : styles.bad)}>
						{sende
							? 'Wird gespeichert …'
							: bestanden
							? `Bestanden — ${richtige} von ${fragen.length} richtig.`
							: `Noch nicht bestanden — ${richtige} von ${fragen.length} richtig, nötig sind ${quizPass}.`}
					</span>
				)}
			</footer>
		</section>
	)
}

Quiz.propTypes = {
	chapter: PropTypes.number.isRequired,
	quizPass: PropTypes.number.isRequired,
	// Bisheriges bestes Ergebnis dieser Person in diesem Kapitel, falls es eines gibt.
	stand: PropTypes.object,
	onSubmit: PropTypes.func.isRequired,
	// Wohin es nach dem Bestehen geht. Fehlt es, bleibt es beim Wiederholen —
	// so verhält sich das letzte Kapitel, hinter dem nichts mehr kommt.
	onWeiter: PropTypes.func,
	weiterLabel: PropTypes.string
}

Quiz.defaultProps = {
	weiterLabel: 'Weiter zum nächsten Kapitel'
}
